/*
 * Place the ecomid "Find your size" widget at the TOP-RIGHT of the size
 * selector (where the old Size chart link was), and keep it working across
 * the theme's in-place product swaps.
 *
 * The hard constraint:
 *   The target spot (the variant-picker option-info row, right side - marked
 *   by .js-size-finder-slot) lives INSIDE the variant picker block, which
 *   has data-block-allow-rerender. Changing size replaces that block's DOM,
 *   so anything we inject there is destroyed. We therefore cannot park the
 *   widget in that spot.
 *
 * The approach:
 *   Keep the ecomid app block in a STABLE location (right after the variant
 *   picker block, where it is not re-rendered) and OVERLAY it onto the slot
 *   with position:absolute. The widget DOM is never destroyed, so its
 *   "Your size: S" result survives size changes. We re-measure the slot
 *   position on resize / re-render / modal open and keep the overlay aligned.
 *
 * Language independence (PT / ES / any locale):
 *   The .js-size-finder-slot marker is rendered server-side by the theme's
 *   variant-picker snippet, but only in some locales (its Liquid condition
 *   compares against a translated label, so it is emitted in English and
 *   skipped in Portuguese / Spanish). When the marker is missing we SYNTHESISE
 *   it here: we locate the size option-info row by structure (it carries a
 *   <legend> and a [data-unselected-label] span, whereas the colour row uses
 *   connected-products markup) and inject our own slot span. This makes the
 *   placement work identically in every language without relying on the
 *   server-rendered marker.
 *
 * Also handled:
 *   - Position jump: a CSS gate (theme.liquid) keeps the block hidden until
 *     we have placed it (.size-finder-positioned).
 *   - Color change (in-place product swap): the swapped-in app block's inline
 *     module script does not execute, so we re-run it to re-mount the widget.
 *   - Size change re-render: the variant block (and any slot inside it) is
 *     replaced, so we re-synthesise the slot and reposition.
 *
 * Works on the product page and on product-card quick-buy modals.
 */
(function () {
  var SLOT_SELECTOR = '.js-size-finder-slot';
  var WIDGET_SELECTOR = '#size-finder';
  var OPTION_INFO_SELECTOR = '.variant-picker__option-info';
  var POSITIONED_CLASS = 'size-finder-positioned';
  var REINIT_FLAG = 'data-ecomid-reinited';
  var SYNTH_FLAG = 'data-size-finder-slot-synth';
  var FAILSAFE_REVEAL_MS = 4000;

  function scopeOf(el) {
    return (
      el.closest('.product-info__block-list') ||
      el.closest('.product-info') ||
      el.closest('product-rerender') ||
      el.closest('quick-buy-modal') ||
      document
    );
  }

  // Find the SIZE variant option-info row inside a scope, language-independently.
  // The size option renders a <legend> plus an unselected-label span
  // ("Select your size"); the colour option uses connected-products markup
  // (a <p> + [data-connected-products-selected-label]) and has neither, so
  // requiring both reliably picks the size row regardless of locale/translation.
  function findSizeOptionInfo(scope) {
    var infos = scope.querySelectorAll(OPTION_INFO_SELECTOR);
    var withLegendAndLabel = [];
    var withLegend = [];
    Array.prototype.forEach.call(infos, function (info) {
      var hasLegend = !!info.querySelector('legend');
      if (!hasLegend) return;
      withLegend.push(info);
      if (info.querySelector('[data-unselected-label]')) withLegendAndLabel.push(info);
    });
    // Prefer the precise signature; fall back to any legend-bearing row.
    return withLegendAndLabel[0] || withLegend[0] || null;
  }

  // Make sure every size-finder scope has a slot to position against. When the
  // server did not render one (non-English locales), create it in the size row.
  function ensureSlots() {
    var widgets = document.querySelectorAll(WIDGET_SELECTOR);
    Array.prototype.forEach.call(widgets, function (widget) {
      var scope = scopeOf(widget);
      // A slot already present (server-rendered or previously synthesised) is fine.
      if (scope.querySelector(SLOT_SELECTOR)) return;
      var sizeInfo = findSizeOptionInfo(scope);
      if (!sizeInfo) return;
      var span = document.createElement('span');
      span.className = 'js-size-finder-slot';
      span.setAttribute(SYNTH_FLAG, 'true');
      sizeInfo.appendChild(span);
    });
  }

  // Re-run the ecomid init script for an app block swapped in after initial
  // load. Re-creating the <script> element makes it execute (inline module
  // scripts inserted via innerHTML do not run on their own).
  function reinitEcomid(appBlock) {
    if (!appBlock || appBlock.hasAttribute(REINIT_FLAG)) return;
    var scripts = appBlock.querySelectorAll('script[type="module"]');
    var reran = false;
    Array.prototype.forEach.call(scripts, function (oldScript) {
      if ((oldScript.textContent || '').indexOf('initSizeFinder') === -1) return;
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(s, oldScript);
      reran = true;
    });
    if (reran) appBlock.setAttribute(REINIT_FLAG, 'true');
  }

  function partsFor(slot) {
    var variantBlock = slot.closest('.product-info__block-item');
    if (!variantBlock || !variantBlock.parentNode) return null;

    var widget = scopeOf(variantBlock).querySelector(WIDGET_SELECTOR);
    if (!widget) return null;

    var appBlock = widget.closest('.product-info__block-item');
    if (!appBlock) return null;

    var container = slot.closest('.product-info__block-list') || variantBlock.parentNode;
    return { variantBlock: variantBlock, appBlock: appBlock, container: container };
  }

  // Overlay the app block on top of the slot (top-right of the size row).
  function placeOverlay(slot, p) {
    // Keep the app block adjacent to the variant picker in the DOM. This is
    // a stable, sensible spot for the failsafe and keeps scope predictable.
    if (p.variantBlock.nextElementSibling !== p.appBlock) {
      p.variantBlock.parentNode.insertBefore(p.appBlock, p.variantBlock.nextSibling);
    }

    // The overlay is absolutely positioned relative to the block list.
    if (getComputedStyle(p.container).position === 'static') {
      p.container.style.position = 'relative';
    }

    var slotRect = slot.getBoundingClientRect();
    var contRect = p.container.getBoundingClientRect();

    // Vertical anchor: the TOP of the "SIZE:" label row, not the slot. The
    // slot is an empty span that sits low inside the row, which pushed the
    // overlay down onto the buttons. Aligning to the row top puts
    // "Your size: S" level with the "SIZE:" label.
    var infoRow = slot.closest(OPTION_INFO_SELECTOR) || slot;
    var rowRect = infoRow.getBoundingClientRect();

    var st = p.appBlock.style;
    st.position = 'absolute';
    st.top = rowRect.top - contRect.top + 'px';
    // Right-align the block's right edge to the slot's right edge (the size
    // row's right edge), mirroring the old Size chart link placement.
    st.right = Math.max(0, contRect.right - slotRect.right) + 'px';
    st.left = 'auto';
    st.margin = '0';
    st.zIndex = '1';
  }

  function process(reinit) {
    ensureSlots();
    var slots = document.querySelectorAll(SLOT_SELECTOR);
    Array.prototype.forEach.call(slots, function (slot) {
      var p = partsFor(slot);
      if (!p) return;
      placeOverlay(slot, p);
      if (reinit) reinitEcomid(p.appBlock);
      p.appBlock.classList.add(POSITIONED_CLASS);
    });
  }

  function reposition() {
    ensureSlots();
    var slots = document.querySelectorAll(SLOT_SELECTOR);
    Array.prototype.forEach.call(slots, function (slot) {
      var p = partsFor(slot);
      if (p) placeOverlay(slot, p);
    });
  }

  var rafScheduled = false;
  function scheduleReposition() {
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(function () {
      rafScheduled = false;
      reposition();
    });
  }

  function init() {
    process(false);

    // Re-measure after layout settles (web fonts, images can shift things).
    scheduleReposition();
    setTimeout(reposition, 300);

    // React to: quick-buy modal opening, color change swapping the product
    // info in place, and size change re-rendering the variant block. Those
    // ADD a slot/widget, OR (for size changes / synthesised locales) replace
    // the variant option-info that holds our slot. We re-process so the slot
    // is (re)synthesised, the widget re-mounted, and the overlay realigned.
    var observer = new MutationObserver(function (mutations) {
      var hit = false;
      for (var i = 0; i < mutations.length && !hit; i++) {
        var m = mutations[i];
        if (m.type !== 'childList') continue;
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (n.nodeType !== 1) continue;
          // Ignore our own synthesised slot to avoid a feedback loop.
          if (n.matches && n.matches(SLOT_SELECTOR) && n.hasAttribute(SYNTH_FLAG)) continue;
          var matches =
            (n.matches &&
              (n.matches(WIDGET_SELECTOR) ||
                n.matches(SLOT_SELECTOR) ||
                n.matches(OPTION_INFO_SELECTOR))) ||
            (n.querySelector &&
              (n.querySelector(WIDGET_SELECTOR) ||
                n.querySelector(SLOT_SELECTOR) ||
                n.querySelector(OPTION_INFO_SELECTOR)));
          if (matches) {
            hit = true;
            break;
          }
        }
      }
      if (hit) process(true);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', scheduleReposition);

    // Failsafe: reveal anything still gated so the widget is never stuck.
    setTimeout(function () {
      var widgets = document.querySelectorAll(WIDGET_SELECTOR);
      Array.prototype.forEach.call(widgets, function (w) {
        var b = w.closest('.product-info__block-item');
        if (b) b.classList.add(POSITIONED_CLASS);
      });
    }, FAILSAFE_REVEAL_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
